// 주간 일정표 #/week — 엑셀의 «날짜별 열 · 시간순 행 · 상태 색» (v0.3 Week.kt · SCREENS §1-5)
// compact 요일 칩 7개 → 그날 카드 1단 · medium 요일 칩 + 카드 2열 · expanded 7열 격자 · large 시간표(시간 행 × 요일 열)
import { useState, type CSSProperties } from "react";
import { itemsFor, statusLabel, statusVar, studentMap } from "../data/select";
import { useView, type View } from "../data/store";
import { St, type Session, type Student } from "../data/types";
import { addDays, dowLabel, dowValue, fmtDate, parseYmd, todayStr } from "../lib/date";
import { useWidthClass } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, Pill, StatusPill, StudentName } from "../ui/parts";
import { Loading } from "./Dashboard";
import { openSheet } from "./sheets";

const monday = (d: string) => addDays(d, 1 - dowValue(d));

export function Week() {
  const v = useView();
  const wc = useWidthClass();
  const td = todayStr();
  const [ws, setWs] = useState(monday(td));
  const [sel, setSel] = useState(td);
  if (!v.status.loaded) return <Loading />;
  const sm = studentMap(v);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const selDay = days.includes(sel) ? sel : days.includes(td) ? td : days[0];
  const byDay = (d: string) => v.sessions.filter((s) => s.date === d && sm.has(s.studentId))
    .sort((a, b) => a.time.localeCompare(b.time) || sm.get(a.studentId)!.name.localeCompare(sm.get(b.studentId)!.name, "ko"));
  const total = days.reduce((n, d) => n + byDay(d).length, 0);

  const bar = (
    <div className="datebar">
      <button type="button" className="btn btn-icon" aria-label="지난주" onClick={() => setWs(addDays(ws, -7))}><Icon name="left" /></button>
      <h2 className="datebar-t">{fmtDate(days[0])} – {fmtDate(days[6])}</h2>
      <button type="button" className="btn btn-icon" aria-label="다음 주" onClick={() => setWs(addDays(ws, 7))}><Icon name="right" /></button>
      {ws !== monday(td) ? <button type="button" className="btn btn-ghost" onClick={() => { setWs(monday(td)); setSel(td); }}>이번 주</button> : null}
      <span className="grow" />
      <span className="muted small nowrap">이번 주 {total}건</span>
    </div>
  );

  return (
    <div className="page week">
      {bar}
      <Legend />
      {wc === "compact" || wc === "medium" ? (
        <>
          <div className="daychips" role="tablist" aria-label="요일">
            {days.map((d) => {
              const n = byDay(d).length;
              return (
                <button key={d} type="button" role="tab" aria-selected={d === selDay} className={"daychip" + (d === selDay ? " on" : "") + (d === td ? " today" : "")} onClick={() => setSel(d)}>
                  <span className="dc-dow">{dowLabel(d)}</span>
                  <span className="dc-day">{parseYmd(d).getDate()}</span>
                  <span className="dc-n">{n ? `${n}명` : "·"}</span>
                </button>
              );
            })}
          </div>
          {byDay(selDay).length === 0 ? <Empty>이날 개별일정이 없습니다.</Empty> : (
            <div className={"card-grid " + (wc === "medium" ? "cg-medium" : "")}>
              {byDay(selDay).map((s) => <WeekRow key={s.id} v={v} s={s} st={sm.get(s.studentId)!} />)}
            </div>
          )}
        </>
      ) : wc === "expanded" ? (
        <div className="week7">
          {days.map((d) => (
            <section key={d} className="week-col" aria-label={fmtDate(d)}>
              <header className={"week-col-h" + (d === td ? " today" : "")}>{fmtDate(d)} · {byDay(d).length}</header>
              {byDay(d).length === 0 ? <p className="muted center small">—</p> : byDay(d).map((s) => <WeekCell key={s.id} v={v} s={s} st={sm.get(s.studentId)!} />)}
            </section>
          ))}
        </div>
      ) : (
        <Timetable v={v} days={days} byDay={byDay} sm={sm} td={td} />
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="legend" aria-label="범례">
      {[St.PLAN, St.DONE, St.PARTIAL, St.NOSHOW].map((s) => (
        <span key={s} className="legend-i"><span className="legend-sw" style={{ background: statusVar(s) }} />{statusLabel(s)}</span>
      ))}
      <span className="legend-i"><Pill color="var(--color-late)">지</Pill>지각</span>
    </div>
  );
}

/** 엑셀 한 칸처럼: 상태 띠 + 시간·이름 + 남은 시험지 */
function WeekCell({ v, s, st }: { v: View; s: Session; st: Student }) {
  const open = itemsFor(v, s).filter((i) => !i.done).length;
  return (
    <button type="button" className={"wcell" + (s.status !== St.PLAN ? " closed" : "")} style={{ "--accent": statusVar(s.status) } as CSSProperties}
      onClick={() => openSheet({ kind: "session", id: s.id })} title={`${st.name} ${st.school} · ${s.time} · ${statusLabel(s.status)}`}>
      <span className="wcell-top"><span className="time">{s.time}</span>{s.late ? <Pill color="var(--color-late)">지</Pill> : null}</span>
      <span className="wcell-name">{st.name}</span>
      <span className="wcell-sub">{s.status === St.PLAN ? `시험지 ${open}` : s.status + (open > 0 && s.status !== St.NOSHOW ? ` · 남음 ${open}` : "")}</span>
    </button>
  );
}

function WeekRow({ v, s, st }: { v: View; s: Session; st: Student }) {
  const open = itemsFor(v, s).filter((i) => !i.done).length;
  return (
    <button type="button" className="row-card wrow" style={{ "--accent": statusVar(s.status) } as CSSProperties} onClick={() => openSheet({ kind: "session", id: s.id })}>
      <span className="time">{s.time}</span>
      <StudentName st={st} />
      <span className="grow" />
      {s.late ? <Pill color="var(--color-late)">지각</Pill> : null}
      {s.status === St.PLAN ? <span className="muted small nowrap">시험지 {open}</span> : <StatusPill status={s.status} />}
    </button>
  );
}

/** large: 왼쪽 시간 축 × 7요일 — 이번 주에 쓰인 시간만 행으로 */
function Timetable({ v, days, byDay, sm, td }: { v: View; days: string[]; byDay: (d: string) => Session[]; sm: Map<string, Student>; td: string }) {
  const times = [...new Set(days.flatMap((d) => byDay(d).map((s) => s.time)))].sort();
  if (!times.length) return <Empty>이번 주 개별일정이 없습니다.</Empty>;
  return (
    <div className="table-wrap" data-scroll-x>
      <table className="tbl timetable">
        <thead>
          <tr><th className="tt-time">시간</th>{days.map((d) => <th key={d} className={d === td ? "today" : ""}>{fmtDate(d)} · {byDay(d).length}</th>)}</tr>
        </thead>
        <tbody>
          {times.map((t) => (
            <tr key={t}>
              <th scope="row" className="tt-time time">{t}</th>
              {days.map((d) => (
                <td key={d}>
                  <div className="tt-cell">{byDay(d).filter((s) => s.time === t).map((s) => <WeekCell key={s.id} v={v} s={s} st={sm.get(s.studentId)!} />)}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
